export type DemoScene = { src: string; title: string };
export type ProjectDemo = { scenes: DemoScene[]; intro: string; features: string[]; platform: string };

// One shared catalog drives both the project overview and each project detail.
// New projects receive a full row, so odd counts never leave an orphan card.
const base = '/assets/project-demos/';
export const projectDemos: Record<string, ProjectDemo> = {
  'msn-ai-radar': {
    platform: 'WEB · LOCAL DESKTOP · AI',
    intro: '从海量收藏中快速梳理出真正值得研究的技术与创作线索。新版雷达将点赞内容分类浏览、价值评估、关键词检索与 AI 对话助手整合在同一工作台，让“随手收藏”真正沉淀为随时可调用的灵感资料库。',
    features: ['分类标签与关键词实时检索，快速定位 AI、模型、工作流等优质内容', '内置 AI 智能对话助手，结合标题与内容摘要直接追问核心逻辑', '单机本地独立运行，支持多维度价值分析与创作线索归档'],
    scenes: [{ src: base + 'radar-synthetic.gif', title: '新版雷达 · 分类浏览与内容检索' }],
  },
  'msn-task-workspace': {
    platform: 'DESKTOP · MOBILE · AI',
    intro: '把想法，变成进展。用统一工作台连接任务、计划、笔记与专注，将大目标拆成眼前可执行的一步，持续保留每一次推进。',
    features: ['项目总览、当前执行与完成记录集中呈现，进度一眼可见', '任务拆解、暂缓与恢复、关联专注形成完整执行路径', '嵌入 AI 助手，新增手机访问入口，延伸桌面工作流'],
    scenes: [{ src: base + 'task-synthetic.gif', title: '新版工作台 · 任务与进度' }],
  },
  'msn-content-publishing': {
    platform: 'CREATIVE · OPERATIONS · AI',
    intro: '从内容创意到真实发布，在一个工作台里完成衔接。让文案、数字人口播、知识动画、横竖封面和多账号发布形成清晰的生产路径。',
    features: ['素材预览与文案编辑协同，统一核对成片和封面', 'AI 助手结合当前任务推进内容处理与制作', '双账号发布流程、任务状态与回执集中追踪'],
    scenes: [{ src: base + 'publishing-synthetic.gif', title: '新版发布工作台 · 内容与素材管理' }],
  },
  'digital-human-ai-review': {
    platform: 'DIGITAL HUMAN · MOTION DESIGN',
    intro: '贯通素材生成、配音与数字人、自动化剪辑包装、封面与发布包到发布回执的内容生产流程。把分散工具连接成可复用的创作链路，代表成品可直接完整播放。',
    features: ['文案与素材生成衔接配音、数字人对口型和镜头编排', '自动化剪辑结合字幕、动态图文与音效包装，保留人工审核', '成片、字幕、横竖封面与发布文案统一交付，衔接发布及回执追踪'],
    scenes: [{ src: base + 'digital-human/technology.gif', title: '自动化剪辑与内容生产 · 完整成品' }],
  },
  'super-msn-ledger': {
    platform: 'ANDROID · PERSONAL FINANCE',
    intro: '围绕日常使用打磨的 Android 记账应用。将收支汇总、账单列表、统计与同步设置组织为连贯的手机操作路径，让数据更容易记录和回看。',
    features: ['本地账本、手工记账与多格式账单导入', '月度收支、结余与分类统计集中呈现', '支付识别生成待确认草稿，可选连接理财 MSN 同步'],
    scenes: [{ src: base + 'ledger/walkthrough.gif', title: 'Android 应用 · 功能模块与统计界面回放' }],
  },
  'msn-study': {
    platform: 'WEB · MOBILE · LEARNING',
    intro: '基于当前成考题库版本，将分科练习、稳拿分方法、英语原文对照、AI 图文讲解与学习计划连成持续学习路径。电脑与手机访问同一套服务，恢复练习位置并衔接复习。',
    features: ['按科目练习与稳拿分方法，英语原文、答案与证据句同页对照', 'AI 多科会话支持图片与联网讲解，题目修改需确认并可追溯', '练习进度恢复、学习计划同步与手机访问，延续电脑端学习路径'],
    scenes: [{ src: base + 'study-synthetic.gif', title: '题库学习系统 · 练习与解析流程' }],
  },
  'finance-motion': {
    platform: 'EDITABLE · DATA · MOTION',
    intro: '让财经概念有清楚的视觉顺序。用可编辑图表、动态标注和分段揭晓，把口播中的量价关系与形态逻辑转成容易跟上的画面。',
    features: ['关键价位、成交量与趋势形态分层呈现', '图解与口播节奏对应，支持字幕及关键词包装', '保留可编辑模板，便于后续替换数据与调整节奏'],
    scenes: [{ src: base + 'finance/breakout.gif', title: '双底颈线突破 · 图解过程' }, { src: base + 'finance/retest.gif', title: '缩量回踩 · 动态标注' }, { src: base + 'finance/invalid.gif', title: '放量跌破 · 失效条件' }],
  },
  'v21-video-editing': {
    platform: 'VIDEO EDITING · WORKFLOW',
    intro: '将文案导入、词级对齐、停顿审核、字幕动效、批量图文音效与智能封面整合为一体化口播视频剪辑管线。支持从粗剪审核到高保真包装全流程交付。',
    features: ['按文稿智能剪辑，词级精确定位并自动标记停顿与语气词', '批量匹配图文插画与音效动效，支持丰富字幕包装预设', '内置便携剪辑环境，快速导出高清成片与多比例封面'],
    scenes: [{ src: base + 'v21/editing.gif', title: '智能剪口播 · 完整界面交互' }, { src: base + 'v21/packaging.gif', title: '批量图文音效包装' }],
  },
  'local-ai-mobile': {
    platform: 'MOBILE · QWEN · COMFYUI',
    intro: '以手机作为轻量化交互入口，连接电脑端本地部署的 Qwen 图像大模型与 ComfyUI 工作流。在移动端随时输入提示词或上传参考图，复用本地工作站算力完成高质量图像生成与编辑。',
    features: ['手机端提交提示词与图片，远程调用电脑算力', '结合本地 Qwen 与 ComfyUI 图像工作流', '支持图集链路与参数预设，结果回传手机'],
    scenes: [{ src: base + 'qwen/mobile.gif', title: '手机端 · 提示词输入与本地工作流' }],
  },
};


